/**
 * Package descriptions are Quill HTML. Pasted text is sometimes stored as a
 * code block (`<pre class="ql-syntax">`) full of non-breaking spaces. A `<pre>`
 * does not wrap and does not participate in the 3-line clamp, so the preview
 * either shows the whole block or grows a horizontal scrollbar.
 *
 * Turn those into normal paragraphs before render or save.
 */
export function normalizePackageDescriptionHtml(html: string | null | undefined): string | null {
  if (!html) {
    return html ?? null;
  }
  const trimmed = html.trim();
  if (!trimmed || trimmed === '<p><br></p>' || trimmed === '<div><br></div>') {
    return null;
  }
  if (typeof DOMParser === 'undefined') {
    return trimmed.replace(/&nbsp;/gi, ' ').replace(/\u00a0/g, ' ');
  }

  const doc = new DOMParser().parseFromString(trimmed, 'text/html');
  unwrapCodeBlocks(doc);
  replaceNbsp(doc);
  clearLockedWhitespace(doc);

  const normalized = doc.body.innerHTML.replace(/>\s+</g, '><').trim();
  if (
    !normalized ||
    normalized === '<p><br></p>' ||
    normalized === '<p></p>' ||
    normalized === '<div><br></div>'
  ) {
    return null;
  }
  return normalized;
}

function unwrapCodeBlocks(doc: Document) {
  doc.querySelectorAll('pre').forEach((pre) => {
    const lines = (pre.textContent || '').replace(/\u00a0/g, ' ').split(/\r?\n/);
    while (lines.length && !lines[0].trim()) {
      lines.shift();
    }
    while (lines.length && !lines[lines.length - 1].trim()) {
      lines.pop();
    }
    if (!lines.length) {
      pre.remove();
      return;
    }
    const fragment = doc.createDocumentFragment();
    lines.forEach((line) => {
      const p = doc.createElement('p');
      if (line.trim()) {
        p.textContent = line.trim();
      } else {
        p.appendChild(doc.createElement('br'));
      }
      fragment.appendChild(p);
    });
    pre.replaceWith(fragment);
  });
}

function replaceNbsp(doc: Document) {
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  let node = walker.nextNode();
  while (node) {
    if (node.nodeValue && node.nodeValue.includes('\u00a0')) {
      node.nodeValue = node.nodeValue.replace(/\u00a0/g, ' ');
    }
    node = walker.nextNode();
  }
}

function clearLockedWhitespace(doc: Document) {
  doc.body.querySelectorAll<HTMLElement>('[style]').forEach((el) => {
    const whiteSpace = el.style.whiteSpace;
    if (whiteSpace && whiteSpace !== 'normal') {
      el.style.removeProperty('white-space');
    }
    if (!el.getAttribute('style')?.trim()) {
      el.removeAttribute('style');
    }
  });
}
